import {Injectable, NgModule, Inject, inject} from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpInterceptorFn, HttpHandlerFn } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { Router } from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

export enum LogLevel {
  None = 0,
  Error = 1,
  Warn = 2,
  Info = 3,
  Debug = 4
}

export interface AuthConfig {
  config: {
    authority: string;
    redirectUrl: string;
    postLogoutRedirectUri: string;
    secureRoutes: string[];
    clientId: string;
    scope: string;
    responseType: string;
    silentRenew: boolean;
    ignoreNonceAfterRefresh: boolean;
    useRefreshToken: boolean;
    logLevel: LogLevel;
    customParamsAuthRequest?: any;
  };
}

@Injectable({ providedIn: 'root' })
export class OidcSecurityService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private config: any;

  constructor(
    @Inject('AUTH_CONFIG') config: any,
    private router: Router
  ) {
    this.config = config;
    localStorage.setItem('auth_config', JSON.stringify(config));
    this.checkAuth();
  }

  checkAuth(url?: string): Observable<boolean> {
    const token = getAccessToken();
    const isAuth = !!token;
    this.isAuthenticatedSubject.next(isAuth);
    return of(isAuth);
  }

  isAuthenticated(): boolean {
    return !!getAccessToken();
  }

  getAccessToken(): string {
    return getAccessToken() || '';
  }

  async authorize(configId?: string): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    const state = this.generateRandomString(32);
    const nonce = this.generateRandomString(32);

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_nonce', nonce);

    const authUrl = `${this.config.authority}/connect/authorize?` +
      `client_id=${this.config.clientId}&` +
      `redirect_uri=${encodeURIComponent(this.config.redirectUrl)}&` +
      `response_type=${this.config.responseType}&` +
      `scope=${encodeURIComponent(this.config.scope)}&` +
      `state=${state}&` +
      `nonce=${nonce}&` +
      `code_challenge=${codeChallenge}&` +
      `code_challenge_method=S256&` +
      `audience=${encodeURIComponent(this.config.customParamsAuthRequest?.audience || '')}&` +
      `breadcrumb=Home&` +
      `prompt=`;

    window.location.href = authUrl;
  }

  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  private generateCodeVerifier(): string {
    return this.generateRandomString(128);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);

    const base64 = btoa(String.fromCharCode(...new Uint8Array(hash)));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  authorizeWithPopUp(configId?: string): Observable<any> {
    return of(null);
  }

  logoff(configId?: string): void {
    localStorage.clear();
    sessionStorage.clear();

    const logoutUrl = `${this.config.authority}/connect/endsession?` +
      `post_logout_redirect_uri=${encodeURIComponent(this.config.postLogoutRedirectUri)}`;

    window.location.href = logoutUrl;
  }

  logoffAndRevokeTokens(configId?: string): Observable<any> {
    this.logoff(configId);
    return of(null);
  }

  getConfiguration(configId?: string): any {
    return this.config;
  }

  getUserData(configId?: string): any {
    const token = getAccessToken();
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  }
}

@Injectable()
export class PublicEventsService {
  private eventSubject = new BehaviorSubject<any>(null);
  public registerForEvents() {
    return this.eventSubject.asObservable();
  }
}

export enum EventTypes {
  ConfigLoaded = 0,
  CheckSessionReceived = 1,
  UserDataChanged = 2,
  NewAuthenticationResult = 3,
  TokenExpired = 4,
  IdTokenExpired = 5,
  SilentRenewStarted = 6
}

@NgModule({})
export class AuthModule {
  static forRoot(config: AuthConfig) {
    return {
      ngModule: AuthModule,
      providers: [
        { provide: 'AUTH_CONFIG', useValue: config.config },
        OidcSecurityService,
        PublicEventsService
      ]
    };
  }
}

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  private config: any;

  constructor() {
  }

  setConfig(config: any) {
    this.config = config;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.config) {
      return next.handle(req);
    }

    const isSecureRoute = this.config.secureRoutes.some((route: string) =>
      req.url.startsWith(route)
    );

    if (!isSecureRoute) {
      return next.handle(req);
    }

    const token = this.getAccessToken();

    if (!token) {
      console.warn('No access token found for secure route:', req.url);
      return next.handle(req);
    }

    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });


    return next.handle(authReq);
  }

  private getAccessToken(): string | null {
    const directToken = localStorage.getItem('access_token');
    if (directToken) {
      return directToken.trim();
    }

    const storageKeys = Object.keys(localStorage);

    for (const key of storageKeys) {
      if (key.includes('authnResult') || key.includes('oidc.user')) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.access_token) {
              return parsed.access_token.trim();
            }
          }
        } catch (e) {
        }
      }
    }

    const sessionKeys = Object.keys(sessionStorage);
    for (const key of sessionKeys) {
      if (key.includes('authnResult') || key.includes('oidc.user')) {
        try {
          const data = sessionStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.access_token) {
              return parsed.access_token.trim();
            }
          }
        } catch (e) {
        }
      }
    }

    return null;
  }
}

export function authInterceptor(): HttpInterceptorFn {
  return (req: HttpRequest<any>, next: HttpHandlerFn) => {
    const configStr = localStorage.getItem('auth_config');
    if (!configStr) {
      console.warn('No auth config found in localStorage for URL:', req.url);
      return next(req);
    }

    const config = JSON.parse(configStr);

    const isSecureRoute = config.secureRoutes.some((route: string) =>
      req.url.startsWith(route)
    );

    if (!isSecureRoute) {
      return next(req);
    }

    const token = getAccessToken();

    if (!token) {
      console.warn('No access token found for secure route:', req.url);
      return next(req);
    }

    // Injected lazily (only for secure routes) — TranslateService's own HTTP loader
    // requests (fetching assets/i18n/*.json) pass through this same interceptor,
    // so injecting it unconditionally at the top caused a circular DI (NG0200).
    const translateService = inject(TranslateService);

    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        currentLang: translateService.currentLang || translateService.getDefaultLang() || 'en'
      }
    });

    return next(authReq);
  };
}

function getAccessToken(): string | null {
  const directToken = localStorage.getItem('access_token');
  if (directToken) {
    return directToken.trim();
  }

  const storageKeys = Object.keys(localStorage);

  for (const key of storageKeys) {
    if (key.includes('authnResult') || key.includes('oidc.user')) {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (parsed.access_token) {
            return parsed.access_token.trim();
          }
        }
      } catch (e) {
      }
    }
  }

  const sessionKeys = Object.keys(sessionStorage);
  for (const key of sessionKeys) {
    if (key.includes('authnResult') || key.includes('oidc.user')) {
      try {
        const data = sessionStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (parsed.access_token) {
            return parsed.access_token.trim();
          }
        }
      } catch (e) {
      }
    }
  }

  return null;
}
